import type React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pause, Play } from 'lucide-react';
import type { Language, VoiceProfile } from '@tekvoice/shared';
import { useAudioPlayer } from '@/components/audio-player';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/sonner';
import { api } from '@/lib/api';
import {
  ReadOnlyNotice,
  SETTINGS_QUERY_KEY,
  SettingSlider,
  useSettingsDraft,
} from '@/pages/settings/settings-form';

const LANGUAGE_LABELS: Record<Language, string> = { EN: 'English', AR: 'Levantine Arabic' };

/** SPEC §8/§11 — voice + model dropdowns with playable previews and bounded sliders. */
export function VoicesSettingsPage(): React.JSX.Element {
  const queryClient = useQueryClient();
  const player = useAudioPlayer();
  const { draft, setDraft, isLoading, readOnly, dirty } = useSettingsDraft('voices');

  /**
   * m10 — EMPLOYEE has `settings.view` (SPEC §9) and reaches this page, but
   * `GET /api/settings/voices` and `/models` are `@Roles('SUPER_ADMIN')`. Firing them
   * anyway produced two silent 403s, so `voices.data` was undefined and every card read
   * "No voice selected yet — generation is blocked" while a voice WAS configured and
   * generation worked fine. Only ask for the catalogue when we are allowed to have it;
   * otherwise fall back to the stored id from `GET /api/settings`, which employees can read.
   */
  const canManage = !readOnly;
  const voices = useQuery({
    queryKey: ['settings', 'voices'],
    queryFn: () => api.settings.voices(),
    enabled: canManage,
  });
  const models = useQuery({
    queryKey: ['settings', 'models'],
    queryFn: () => api.settings.models(),
    enabled: canManage,
  });

  const save = useMutation({
    mutationFn: () => {
      if (!draft) throw new Error('Nothing to save');
      return api.settings.update({ voices: { en: draft.en, ar: draft.ar } });
    },
    onSuccess: async (result) => {
      setDraft(result.data.voices);
      toast.success('Voice settings saved');
      await queryClient.invalidateQueries({ queryKey: SETTINGS_QUERY_KEY });
      await queryClient.invalidateQueries({ queryKey: ['settings', 'status'] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  if (isLoading || !draft) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  const update = (language: 'en' | 'ar', patch: Partial<VoiceProfile>): void => {
    setDraft((previous) =>
      previous ? { ...previous, [language]: { ...previous[language], ...patch } } : previous,
    );
  };

  const languageCard = (key: 'en' | 'ar', language: Language): React.JSX.Element => {
    const profile = draft[key];
    const selectedVoice = voices.data?.voices.find((voice) => voice.voiceId === profile.voiceId);
    const previewKey = `voice-preview-${profile.voiceId}`;
    const previewPlaying = player.isPlaying(previewKey);
    const hasVoice = profile.voiceId.trim().length > 0;

    return (
      <Card key={key}>
        <CardHeader>
          <CardTitle>{LANGUAGE_LABELS[language]}</CardTitle>
          <CardDescription>
            {selectedVoice
              ? `${selectedVoice.name}${selectedVoice.description ? ` — ${selectedVoice.description}` : ''}`
              : hasVoice
                ? `Voice ${profile.voiceId}`
                : 'No voice selected yet — generation is blocked until one is chosen.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor={`voice-${key}`}>Voice</Label>
            {canManage ? (
              <div className="flex items-center gap-2">
                <Select
                  value={profile.voiceId || undefined}
                  disabled={voices.isLoading}
                  onValueChange={(value) => update(key, { voiceId: value })}
                >
                  <SelectTrigger id={`voice-${key}`} className="flex-1">
                    <SelectValue placeholder="Choose a voice…" />
                  </SelectTrigger>
                  <SelectContent>
                    {(voices.data?.voices ?? []).map((voice) => (
                      <SelectItem key={voice.voiceId} value={voice.voiceId}>
                        {voice.name}
                        {voice.category ? ` · ${voice.category}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="icon"
                  aria-label={`Preview ${LANGUAGE_LABELS[language]} voice`}
                  disabled={!selectedVoice?.previewUrl}
                  onClick={() => {
                    if (selectedVoice?.previewUrl) player.toggle(previewKey, selectedVoice.previewUrl);
                  }}
                >
                  {previewPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                </Button>
              </div>
            ) : (
              <p id={`voice-${key}`} className="text-sm tabular-nums" data-testid={`voice-readonly-${key}`}>
                {hasVoice ? profile.voiceId : 'Not configured'}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor={`model-${key}`}>Model</Label>
            {canManage ? (
              <Select
                value={profile.modelId}
                disabled={models.isLoading}
                onValueChange={(value) => update(key, { modelId: value as VoiceProfile['modelId'] })}
              >
                <SelectTrigger id={`model-${key}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(models.data?.models ?? []).map((model) => (
                    <SelectItem key={model.modelId} value={model.modelId}>
                      {model.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p id={`model-${key}`} className="text-sm" data-testid={`model-readonly-${key}`}>
                {profile.modelId}
              </p>
            )}
          </div>

          <SettingSlider
            id={`stability-${key}`}
            label="Stability"
            description="Higher is steadier; lower is more expressive."
            value={profile.stability}
            min={0}
            max={1}
            step={0.05}
            disabled={readOnly}
            format={(value) => value.toFixed(2)}
            onChange={(value) => update(key, { stability: value })}
          />
          <SettingSlider
            id={`similarity-${key}`}
            label="Similarity boost"
            value={profile.similarityBoost}
            min={0}
            max={1}
            step={0.05}
            disabled={readOnly}
            format={(value) => value.toFixed(2)}
            onChange={(value) => update(key, { similarityBoost: value })}
          />
          <SettingSlider
            id={`style-${key}`}
            label="Style"
            value={profile.style}
            min={0}
            max={1}
            step={0.05}
            disabled={readOnly}
            format={(value) => value.toFixed(2)}
            onChange={(value) => update(key, { style: value })}
          />
          <SettingSlider
            id={`speed-${key}`}
            label="Speed"
            description="0.7 – 1.2 ×"
            value={profile.speed}
            min={0.7}
            max={1.2}
            step={0.01}
            disabled={readOnly}
            format={(value) => `${value.toFixed(2)}×`}
            onChange={(value) => update(key, { speed: value })}
          />

          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor={`boost-${key}`}>Speaker boost</Label>
              <p className="text-xs text-muted-foreground">Slightly slower, closer to the voice.</p>
            </div>
            <Switch
              id={`boost-${key}`}
              checked={profile.useSpeakerBoost}
              disabled={readOnly}
              onCheckedChange={(checked) => update(key, { useSpeakerBoost: checked })}
            />
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-4">
      <ReadOnlyNotice readOnly={readOnly} />
      <div className="grid gap-4 lg:grid-cols-2">
        {languageCard('en', 'EN')}
        {languageCard('ar', 'AR')}
      </div>
      {!readOnly ? (
        <div className="flex items-center gap-3">
          <Button disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save voices'}
          </Button>
          {dirty ? <span className="text-sm text-muted-foreground">Unsaved changes</span> : null}
        </div>
      ) : null}
    </div>
  );
}
